import { useState, useEffect } from "react";
import { Modal } from "../../ui/modal";
import Alert from "../../ui/alert/Alert";
import Spinner from "../../ui/Spinner/Spinner";
import { useCurrencyStore } from "../../../stores/useCurrencyStore";
import { Wallet, Transaction } from "../types";
import { BankTransferDetailsModal } from "./BankTransferDetailsModal";
import { fetchCountryConfig } from "../API/services";
import { fetchMobileNetworks } from "../../Transactions/Apis/PaymentMethods";
import { MobileMoneyModal } from "./MobileMoneyModal";
import { AlertCircle, ChevronDownIcon } from "lucide-react";
import { CONFIG_TYPE, WALLET_MOBILE_MONEY_CURRENCIES } from "../../../utils/constant";
import { fetchTransactionFee } from "../../Transactions/Apis/exchangeRates";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  wallet: Wallet | null;
  onSuccess: (transaction?: Transaction) => void;
}

const FundWalletModal = ({ isOpen, onClose, wallet, onSuccess }: Props) => {
  const [amount, setAmount] = useState("");
  const [displayAmount, setDisplayAmount] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [showBankDetails, setShowBankDetails] = useState(false);
  const [showMobileMoney, setShowMobileMoney] = useState(false);
  const [calculatedAmounts, setCalculatedAmounts] = useState({
    fee: 0,
    amountToReceive: 0,
    total: 0
  });
  const [countryConfig, setCountryConfig] = useState<any>(null);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [providers, setProviders] = useState<any[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isAmountValid, setIsAmountValid] = useState(false);
  
  // New states for supporting countries
  const [supportingCountries, setSupportingCountries] = useState<any[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<any>(null);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [selectedCurrencyData, setSelectedCurrencyData] = useState<any>(null);

  const { walletSupportedCurrencies } = useCurrencyStore();
  const baseCurrencyData = walletSupportedCurrencies.find(c => c.code === wallet?.currency);
  const hasSupportingCountries = baseCurrencyData?.hasSupportingCountries;
  
  // ---- Load supporting countries when modal opens for currencies that have them
  useEffect(() => {
    if (isOpen && wallet?.currency && baseCurrencyData) {
      if (hasSupportingCountries && baseCurrencyData?.supportingCountries) {
        // Convert supportingCountries object to array and filter by allowWalletCreation
        const countriesArray = Object.values(baseCurrencyData.supportingCountries)
          .filter((country: any) => country.allowWalletCreation === true);
        
        setSupportingCountries(countriesArray);
        
        // If there's only one country, preselect it (like XAF)
        if (countriesArray.length === 1) {
          setSelectedCountry(countriesArray[0]);
          setSelectedCurrencyData(countriesArray[0]);
        } else if (countriesArray.length > 1) {
          // For XOF with multiple countries, don't auto-select
          // Just set the base currency data initially
          setSelectedCurrencyData(baseCurrencyData);
        }
      } else {
        // For currencies without supporting countries, use the base currency data
        setSelectedCurrencyData(baseCurrencyData);
      }
    }
  }, [isOpen, wallet?.currency, baseCurrencyData, hasSupportingCountries]);

  // ---- Fetch config and providers when selected country changes
  useEffect(() => {
    const loadConfig = async () => {
      // For currencies with supporting countries, only fetch config when a country is selected
      if (hasSupportingCountries && !selectedCountry) {
        // Don't fetch config yet for currencies with multiple countries
        setCountryConfig(null);
        return;
      }
      
      // Determine which data to use for fetching config
      const dataToUse = hasSupportingCountries && selectedCountry ? selectedCountry : selectedCurrencyData;
      
      if (!dataToUse?.iso2 || !isOpen) return;
      
      setLoadingConfig(true);
      setError(null);
      setValidationErrors([]);
      
      try {
        // Fetch country config using selected country's iso2
        const config = await fetchCountryConfig(dataToUse.iso2);
        if (config) {
          setCountryConfig(config);
        } else {
          setError("Failed to load fee configuration");
        }

        // Fetch mobile networks if the currency has mobile money
        const hasMobileMoney = dataToUse?.paymentMethod?.mobileMoney || 
                              dataToUse?.hasMomo ||
                              WALLET_MOBILE_MONEY_CURRENCIES.includes(wallet?.currency || '');
        
        if (hasMobileMoney) {
          try {
            const networks = await fetchMobileNetworks(
              dataToUse.code || wallet?.currency,
              dataToUse.iso2
            );

            // Filter out networks that don't support wallet creation
            const filteredNetworks = networks.filter((network: any) => {
              if (dataToUse.code === "KES" && network?.name === "AIRTEL") {
                return false;
              }
              return true;
            });
            
            setProviders(filteredNetworks);
          } catch (networkError) {
            console.error("Failed to load mobile networks:", networkError);
            setProviders([]);
          }
        } else {
          setProviders([]);
        }
      } catch (err) {
        setError("Failed to load configuration");
        console.error("Error loading data:", err);
      } finally {
        setLoadingConfig(false);
      }
    };

    if (isOpen) {
      loadConfig();
    }
  }, [
    selectedCountry?.iso2, 
    selectedCurrencyData?.iso2, 
    isOpen, 
    wallet?.currency, 
    hasSupportingCountries
  ]);

  // Reset states when modal closes
  useEffect(() => {
    if (!isOpen) {
      setAmount("");
      setDisplayAmount("");
      setError(null);
      setValidationErrors([]);
      setShowBankDetails(false);
      setShowMobileMoney(false);
      setCalculatedAmounts({ fee: 0, amountToReceive: 0, total: 0 });
      setProviders([]);
      setIsAmountValid(false);
      setSupportingCountries([]);
      setSelectedCountry(null);
      setShowCountryDropdown(false);
      setSelectedCurrencyData(null);
      setCountryConfig(null);
    }
  }, [isOpen]);

  // ---- Recalculate and validate when amount or config changes
  useEffect(() => {
    if (amount && countryConfig) {
      calculateAmounts();
      validateAmount();
    } else {
      setCalculatedAmounts({ fee: 0, amountToReceive: 0, total: 0 });
      setValidationErrors([]);
      setIsAmountValid(false);
    }
  }, [amount, countryConfig]);

  if (!wallet || !baseCurrencyData) return null;
 
  const isNGN = wallet.currency === 'NGN';
  const isMobileMoney = WALLET_MOBILE_MONEY_CURRENCIES.includes(wallet.currency);

    // ---- Determine which data to use for display
  const dataToUse = hasSupportingCountries && selectedCountry ? selectedCountry : selectedCurrencyData;

  // ---- Calculate fee, amount to receive, and total
const calculateAmounts = async () => {
  const inputAmount = parseFloat(amount);
  if (isNaN(inputAmount) || inputAmount <= 0) {
    setCalculatedAmounts({ fee: 0, amountToReceive: 0, total: 0 });
    return;
  }

  const fee = await fetchTransactionFee(inputAmount, dataToUse?.iso2 || '', CONFIG_TYPE.DEPOSIT_FEE);

  const amountToReceive = inputAmount - fee;
  const total = inputAmount;

  setCalculatedAmounts({
    fee,
    amountToReceive,
    total,
  });
};
 
  // ---- Validate amount against config
  const validateAmount = () => {
    const inputAmount = parseFloat(amount);
    const errors: string[] = [];
    let isValid = true;

    // Check if amount is empty or invalid
    if (!amount || isNaN(inputAmount) || inputAmount <= 0) {
      setIsAmountValid(false);
      setValidationErrors([]);
      return false;
    }

    // Check minimum deposit
    if (countryConfig?.minDeposit) {
      const minDeposit = parseFloat(countryConfig.minDeposit.value);
      if (inputAmount < minDeposit) {
        isValid = false;
      }
    }

    // Check maximum deposit
    if (countryConfig?.maxDeposit) {
      const maxDeposit = parseFloat(countryConfig.maxDeposit.value);
      if (inputAmount > maxDeposit) {
        isValid = false;
      }
    }

    // Check min/max range
    if (countryConfig?.minDeposit && countryConfig?.maxDeposit && errors.length === 0) {
      const minDeposit = parseFloat(countryConfig.minDeposit.value);
      const maxDeposit = parseFloat(countryConfig.maxDeposit.value);
      if (inputAmount < minDeposit || inputAmount > maxDeposit) {
        errors.push(`Amount must be between ${wallet.symbol}${minDeposit.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })} and ${wallet.symbol}${maxDeposit.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })}`);
        isValid = false;
      }
    }

    setValidationErrors(errors);
    setIsAmountValid(isValid && errors.length === 0);
    
    return isValid;
  };

  const formatNumberWithCommas = (value: string): string => {
    if (!value) return "";
    const cleanValue = value.replace(/[^\d.]/g, "");
    const parts = cleanValue.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.length > 1 ? `${parts[0]}.${parts[1]}` : parts[0];
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    const cleanValue = inputValue.replace(/[^\d.,]/g, "");
    
    const decimalParts = cleanValue.split(".");
    let processedValue = cleanValue;
    if (decimalParts.length > 2) {
      processedValue = decimalParts[0] + "." + decimalParts.slice(1).join("");
    }
    
    const rawValue = processedValue.replace(/,/g, "");
    
    if (rawValue === "" || /^\d*\.?\d*$/.test(rawValue)) {
      setAmount(rawValue);
      setDisplayAmount(formatNumberWithCommas(rawValue));
      setError("");
    }
  };

  const handleContinue = async () => {
    if (!validateAmount()) return;
    
    // ---- For currencies with supporting countries, require country selection
    if (hasSupportingCountries && !selectedCountry) {
      setError("Please select a country");
      return;
    }

    const dataToUse = hasSupportingCountries && selectedCountry ? selectedCountry : selectedCurrencyData;

    if (isNGN) {
      // ---- For NGN, show bank transfer details modal
      setShowBankDetails(true);
    } else if (isMobileMoney || dataToUse?.paymentMethod?.mobileMoney || dataToUse?.hasMomo) {
      // ---- For mobile money currencies, show mobile money modal
      setShowMobileMoney(true);
    } else {
      // ---- For other currencies with card payment
      await handleCardPayment();
    }
  };

  const handleCardPayment = async () => {
    // TODO: Implement card payment logic
    await new Promise(resolve => setTimeout(resolve, 1000));
    onSuccess();
    onClose();
  };

  const handlePaymentComplete = (transaction?: Transaction) => {
    setShowBankDetails(false);
    setShowMobileMoney(false);
    onSuccess(transaction);
    onClose();
  };

  const renderFeeAndAmounts = () => {
    if (!amount || parseFloat(amount) <= 0 || !countryConfig) return null;

    return (
      <div className="mb-6 space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
        <div className="flex justify-between">
          <span className="text-sm text-gray-600 dark:text-gray-400">Fee:</span>
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            {wallet.symbol} {calculatedAmounts.fee.toFixed(2)}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-gray-600 dark:text-gray-400">Amount to Receive:</span>
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            {wallet.symbol} {calculatedAmounts.amountToReceive.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}
          </span>
        </div>
        <div className="flex justify-between border-t border-gray-200 pt-3 dark:border-gray-700">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Total:</span>
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            {wallet.symbol} {calculatedAmounts.total.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}
          </span>
        </div>
      </div>
    );
  };

  return (
    <>
      <Modal
        isOpen={isOpen && !showBankDetails && !showMobileMoney}
        onClose={onClose}
        className="max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-800 dark:bg-gray-900 dark:shadow-2xl"
      >
        <h2 className="mb-2 text-lg font-semibold text-gray-800 dark:text-white/90">
          Fund {wallet.name} Wallet
        </h2>
        
        <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
          <div className="mt-2 flex flex-wrap justify-between text-sm">
            <div className="text-gray-500 dark:text-gray-400">
              Current balance: {wallet.symbol} {wallet.balance.toLocaleString()}
            </div>
          </div>
        </p>

        {error && (
          <div className="mb-4">
            <Alert 
              variant="error" 
              title="Error" 
              message={error}
            />
          </div>
        )}

        {/* Country selection for currencies with supporting countries */}
        {hasSupportingCountries && supportingCountries.length > 0 && (
          <div className="mb-6">
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Select Country
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                className="flex w-full items-center rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-gray-800 hover:bg-gray-100 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700"
              >
                {selectedCountry ? (
                  <>
                    <img
                      src={selectedCountry.flag}
                      alt={selectedCountry.country}
                      className="mr-2 h-5 w-5 rounded-full"
                    />
                    <span>{selectedCountry.country}</span>
                    <span className="ml-1 text-sm text-gray-500 dark:text-gray-400">
                      (+{selectedCountry.dialCode})
                    </span>
                  </>
                ) : (
                  <span className="text-gray-400 dark:text-gray-500">
                    Select a country
                  </span>
                )}
                <ChevronDownIcon className="ml-auto h-5 w-5 text-gray-400" />
              </button>

              {showCountryDropdown && (
                <div className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                  {supportingCountries.map((country) => (
                    <div
                      key={`${country.code}-${country.iso2}`}
                      onClick={() => {
                        setSelectedCountry(country);
                        setSelectedCurrencyData(country);
                        setShowCountryDropdown(false);
                        // Don't reset amount when re-selecting same country
                        if (selectedCountry?.iso2 !== country.iso2) {
                          setAmount("");
                          setDisplayAmount("");
                          setCalculatedAmounts({ fee: 0, amountToReceive: 0, total: 0 });
                        }
                      }}
                      className="flex cursor-pointer items-center px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <img
                        src={country.flag}
                        alt={country.country}
                        className="mr-2 h-5 w-5 rounded-full"
                      />
                      <div className="flex flex-col">
                        <span className="text-gray-700 dark:text-white">
                          {country.country}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          +{country.dialCode}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Loading state - only show when loading config */}
        {loadingConfig && (
          <div className="mb-6 flex justify-center">
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Spinner className="h-4 w-4" />
              Loading...
            </div>
          </div>
        )}

        {/*Show if: - No supporting countries,Supporting countries with a selected country, Config loaded
        */}
        {(!hasSupportingCountries || selectedCountry) && (
          <>
            <div className="mb-6">
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Amount ({wallet.symbol})
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={displayAmount}
                  onChange={handleAmountChange}
                  placeholder="0.00"
                  disabled={loadingConfig || (hasSupportingCountries && !selectedCountry)}
                  className={`w-full rounded-lg border bg-gray-50 pl-12 pr-4 py-3 text-lg text-gray-900 transition-colors focus:outline-none focus:ring-2 disabled:opacity-50 dark:bg-gray-800 dark:text-white ${
                    validationErrors.length > 0 
                      ? "border-red-300 focus:border-red-500 focus:ring-red-500/30 dark:border-red-700" 
                      : "border-gray-200 focus:border-primary/50 focus:ring-primary/30 dark:border-gray-700"
                  }`}
                />
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
                  {wallet.symbol}
                </span>
              </div>
              
              {/* Validation error messages */}
              {validationErrors.length > 0 && (
                <div className="mt-2 space-y-1">
                  {validationErrors.map((errorMsg, index) => (
                    <div 
                      key={index} 
                      className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400"
                    >
                      <AlertCircle className="h-4 w-4 flex-shrink-0" />
                      <span>{errorMsg}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Fee and Amounts Breakdown - only show when config is loaded */}
            {countryConfig && renderFeeAndAmounts()}

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <button
                onClick={onClose}
                disabled={loadingConfig}
                className="rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              
              <button
                onClick={handleContinue}
                disabled={
                  loadingConfig || 
                  !amount || 
                  parseFloat(amount) <= 0 || 
                  !isAmountValid || 
                  validationErrors.length > 0 ||
                  (hasSupportingCountries && !selectedCountry)
                }
                className="rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Continue
              </button>
            </div>
          </>
        )}
      </Modal>

      {/* Bank Transfer Details Modal for NGN */}
      {isNGN && wallet && dataToUse && (
        <BankTransferDetailsModal
          isOpen={showBankDetails}
          onClose={() => setShowBankDetails(false)}
          onPaymentComplete={handlePaymentComplete}
          wallet={wallet}
          amount={parseFloat(amount)}
          fee={calculatedAmounts.fee}
        />
      )}

      {/* Mobile Money Modal for mobile money currencies */}
      {(isMobileMoney || dataToUse?.paymentMethod?.mobileMoney) && 
        wallet && 
        dataToUse && (
        <MobileMoneyModal
          isOpen={showMobileMoney}
          onClose={() => setShowMobileMoney(false)}
          onPaymentComplete={handlePaymentComplete}
          wallet={wallet}
          amount={parseFloat(amount)}
          fee={calculatedAmounts.fee}
          providers={providers}
          currencyData={dataToUse}
        />
      )}
    </>
  );
};

export default FundWalletModal;